import React, { useState, useEffect } from 'react';
import { Drawer, Box, Typography, IconButton, Grid, TextField, Button, Divider } from '@mui/material';
import { OfferStandaloneInquiryDTO } from '../../services/offerStandaloneManageService';
import { offerStandaloneManageService } from '../../services/offerStandaloneManageService';
import { useNotification } from '../layouts/Layout';

interface InquiryNotesDrawerProps {
  open: boolean;
  onClose: () => void;
  inquiry: OfferStandaloneInquiryDTO | null;
  offerId: string;
  packageName?: string;
  onUpdated?: () => void;
}

const fieldLabelStyle = { color: '#666', fontSize: '0.75rem', fontFamily: 'Roboto, sans-serif' } as const;
const fieldValueStyle = { fontWeight: 500, fontSize: '0.9rem', fontFamily: 'Roboto, sans-serif', color: '#2D2D32' } as const;

const InquiryNotesDrawer: React.FC<InquiryNotesDrawerProps> = ({ open, onClose, inquiry, offerId, packageName, onUpdated }) => {
  const { showNotification } = useNotification();
  const [notes, setNotes] = useState<string>('');
  const [contractorPhone1, setContractorPhone1] = useState<string>('');
  const [contractorPhone2, setContractorPhone2] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && inquiry) {
      setNotes(inquiry.notes || '');
      setContractorPhone1(inquiry.contractor_phone_1 || '');
      setContractorPhone2(inquiry.contractor_phone_2 || '');
    } else {
      setNotes('');
      setContractorPhone1('');
      setContractorPhone2('');
    }
  }, [open, inquiry]);

  const handleSave = async () => {
    if (!inquiry) return;
    
    setSaving(true);
    try {
      await offerStandaloneManageService.updateInquiry(offerId, inquiry.inquiry_id, {
        notes: notes.trim() || null,
        contractor_phone_1: contractorPhone1.trim() || null,
        contractor_phone_2: contractorPhone2.trim() || null
      } as Parameters<typeof offerStandaloneManageService.updateInquiry>[2]);
      showNotification('Notatki i numery telefonów zapisane.', 'success');
      if (onUpdated) {
        onUpdated();
      }
      onClose();
    } catch (error: unknown) {
      console.error('Błąd podczas zapisywania notatek:', error);
      const err = error as { response?: { data?: { error?: string } } };
      showNotification(err?.response?.data?.error || 'Nie udało się zapisać notatek i numerów telefonów.', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!inquiry) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 680, maxWidth: '100vw', borderRadius: 0 } }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ pl: 4, pr: 4, pt: '22px', pb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '18px', color: '#2D2D32' }}>
            Notatki
          </Typography>
          <IconButton 
            onClick={onClose} 
            size="small"
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 30,
              minHeight: 30,
              '& > *': {
                pointerEvents: 'none',
              },
            }}
          >
            <Box 
              component="img" 
              src="/assets/close.svg" 
              alt="Zamknij" 
              sx={{ 
                width: 13, 
                height: 13, 
                display: 'block',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
              }} 
            />
          </IconButton>
        </Box>
        <Box sx={{ px: 4, pt: '36px', pb: 4, flex: 1, overflowY: 'auto' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography sx={fieldLabelStyle}>ID zapytania</Typography>
              <Typography sx={fieldValueStyle}>{inquiry.inquiry_id}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography sx={fieldLabelStyle}>E-mail</Typography>
              <Typography sx={fieldValueStyle}>{inquiry.contractor_email || '-'}</Typography>
            </Grid>
            {packageName && (
              <Grid item xs={12} md={6}>
                <Typography sx={fieldLabelStyle}>Pakiet</Typography>
                <Typography sx={fieldValueStyle}>{packageName}</Typography>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <Typography sx={fieldLabelStyle}>Temat</Typography>
              <Typography sx={fieldValueStyle}>{inquiry.subject || '-'}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography sx={fieldLabelStyle}>Status</Typography>
              <Typography sx={fieldValueStyle}>{inquiry.status || '-'}</Typography>
            </Grid>
            {inquiry.created_at && (
              <Grid item xs={12} md={6}>
                <Typography sx={fieldLabelStyle}>Data utworzenia</Typography>
                <Typography sx={fieldValueStyle}>
                  {new Date(inquiry.created_at).toLocaleDateString('pl-PL')}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <Typography sx={fieldLabelStyle}>Nr telefonu 1</Typography>
              <TextField
                fullWidth
                size="small"
                value={contractorPhone1}
                onChange={(e) => setContractorPhone1(e.target.value)}
                placeholder="+48 123 456 789"
                variant="outlined"
                sx={{
                  mt: 0.5,
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    backgroundColor: '#fff',
                    '& fieldset': { borderColor: '#d4d4d9' },
                    '&:hover fieldset': { borderColor: '#595966' },
                    '&.Mui-focused fieldset': { borderColor: '#2020AF' },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography sx={fieldLabelStyle}>Nr telefonu 2</Typography>
              <TextField
                fullWidth
                size="small"
                value={contractorPhone2}
                onChange={(e) => setContractorPhone2(e.target.value)}
                placeholder="+48 123 456 789"
                variant="outlined"
                sx={{
                  mt: 0.5,
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    backgroundColor: '#fff',
                    '& fieldset': { borderColor: '#d4d4d9' },
                    '&:hover fieldset': { borderColor: '#595966' },
                    '&.Mui-focused fieldset': { borderColor: '#2020AF' },
                  },
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography sx={{ ...fieldLabelStyle, mb: 1 }}>Notatki</Typography>
              <TextField
                fullWidth
                multiline
                rows={12}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Wpisz notatki dotyczące tego zapytania..."
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    color: '#2D2D32',
                    backgroundColor: '#fff',
                    '& fieldset': {
                      borderColor: '#d4d4d9',
                    },
                    '&:hover fieldset': {
                      borderColor: '#595966',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#2020AF',
                    },
                  },
                }}
              />
            </Grid>
          </Grid>
        </Box>
        <Divider />
        <Box sx={{ px: 4, py: 2, display: 'flex', justifyContent: 'flex-end', gap: 1, boxShadow: '0px 4px 4px rgba(0,0,0,0.05)' }}>
          <Button
            onClick={onClose}
            variant="text"
            sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              textTransform: 'none',
              color: '#2D2D32',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            Anuluj
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            sx={{
              backgroundColor: '#020242',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#030360'
              },
              '&:disabled': {
                backgroundColor: 'rgba(0, 0, 0, 0.12)',
                color: 'rgba(0, 0, 0, 0.26)'
              }
            }}
          >
            {saving ? 'Zapisywanie...' : 'Zapisz'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default InquiryNotesDrawer;
